'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';

export function RegisterPage() {
  const { checkAuth } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== password2) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || undefined,
          password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo crear la cuenta');
      toast.success('¡Cuenta creada! Bienvenido a LIVE MOTO');
      await checkAuth();
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-4 mb-4">
            <img
              src="/images/logo-live-moto.png"
              alt="LIVE MOTO Logo"
              className="w-48 h-auto object-contain"
            />
            <div className="text-center">
              <h1 className="text-3xl font-bold">
                <span className="text-[#E31B23]">LIVE</span>{' '}
                <span className="text-[#C0C0C0]">MOTO</span>
              </h1>
              <p className="text-[#FF4500] font-medium mt-1">Crear cuenta</p>
            </div>
          </div>
          <p className="text-slate-400">Regístrate para gestionar tus motos y servicios</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
          <form onSubmit={handleSubmit}>
            <CardContent className="pt-6 space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Nombre completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre y apellido"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                  maxLength={255}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300">Teléfono <span className="text-slate-500 text-xs">(opcional)</span></Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+58 412 1234567"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  maxLength={20}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                  minLength={6}
                  maxLength={128}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password2" className="text-slate-300">Confirmar contraseña</Label>
                <Input
                  id="password2"
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                  minLength={6}
                  maxLength={128}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#E31B23] to-[#FF4500] hover:from-[#FF2D35] hover:to-[#FF6B35] text-white font-semibold"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Crear cuenta
              </Button>
            </CardContent>
          </form>
        </Card>

        <p className="mt-6 text-center text-slate-400 text-sm">
          ¿Ya tienes cuenta?{' '}
          <Link href="/login" className="text-[#FF4500] hover:text-[#FF6B35] font-semibold">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
